import { useMemo } from 'react';
import { addDays, isBefore, isSameDay, isWithinInterval } from 'date-fns';

import { Query } from '../../design-system/types';
import { orderByActionType, TRIP_HAS_ENDED_STEP } from '../../domain';
import { DisplayDossierOverviews } from '../dossier/types';
import { useGetDossierOverviews } from '../dossier-overview/hooks';
import { useAuth } from '../../shared/providers/AuthProvider';

import { formatDossierAndFilterByQuery, getPriorityFromDossier } from './operations';
import { DisplayDossierOverviewsGroupedByPriority, Period } from './types';

const filterByDay =
    (period: Period, currentDate: number) =>
    (dossiers: DisplayDossierOverviews): DisplayDossierOverviews =>
        dossiers.filter(({ task: { dueDate } }) => {
            switch (period) {
                case 'today':
                    return isSameDay(dueDate, currentDate) || isBefore(dueDate, currentDate);
                case 'tomorrow':
                    return isSameDay(dueDate, addDays(currentDate, 1));
                case 'this_week':
                    return isWithinInterval(dueDate, {
                        start: addDays(currentDate, 2),
                        end: addDays(currentDate, 8),
                    });
                default:
                    return false;
            }
        });

const sortPriority = (dossiers: DisplayDossierOverviews) => dossiers.sort(orderByActionType);

const filterOpenedDossiers = (dossierOverviews: DisplayDossierOverviews) =>
    dossierOverviews.filter((dossierOverview) => !dossierOverview.archiveDate);

const filterDossiersNotInTripHasEndedStep = (dossierOverviews: DisplayDossierOverviews) =>
    dossierOverviews.filter((dossierOverview) => dossierOverview.step !== TRIP_HAS_ENDED_STEP);

const groupDossiersByPriority =
    (currentDate: number) =>
    (dossiers: DisplayDossierOverviews): DisplayDossierOverviewsGroupedByPriority => {
        const groups = {
            high: [],
            medium: [],
            low: [],
        } as DisplayDossierOverviewsGroupedByPriority;
        for (const dossier of dossiers) {
            const priority = getPriorityFromDossier(dossier, currentDate);
            groups[priority].push(dossier);
        }
        return groups;
    };

const useFormattedDossiersByPeriod = (period: Period, query: Query, currentDate: number) => {
    const { data: dossiers } = useGetDossierOverviews();
    const { userId } = useAuth();

    return useMemo(() => {
        if (!dossiers) return [];

        const formattedDossiers = formatDossierAndFilterByQuery(query, userId)(dossiers);
        const filteredDossiers = filterOpenedDossiers(formattedDossiers);
        const filteredDossiersNotInTrip = filterDossiersNotInTripHasEndedStep(filteredDossiers);
        const filteredByDay = filterByDay(period, currentDate)(filteredDossiersNotInTrip);
        return sortPriority(filteredByDay);
    }, [dossiers, period, query, userId, currentDate]);
};

export const useGroupDossiersByPeriodAndPriorities = (
    period: Period,
    query: Query,
    currentDate: number,
) => {
    const formattedDossiers = useFormattedDossiersByPeriod(period, query, currentDate);

    return useMemo(
        () => groupDossiersByPriority(currentDate)(formattedDossiers),
        [formattedDossiers, currentDate],
    );
};
